import * as React from "react";
import { Link } from "react-router-dom";
import { useMe } from "../hooks/useMe.js";
import { AuthMenu } from "./AuthMenu.js";
import { StreakBadge } from "./StreakBadge.js";
import { Drawer } from "./mobile/Drawer.js";
import { NAV_LINKS } from "../lib/nav-links.js";

interface FieldNavProps {
  /**
   * Override the right-side CTA for anonymous visitors. Pages that are
   * funnels into a specific action (e.g. placement results → /plans) pass
   * their own label + dest; everyone else inherits the default Sign in /
   * /signin pair. When the user is authenticated, the CTA is replaced by
   * the avatar dropdown regardless of these overrides.
   */
  ctaLabel?: string;
  ctaTo?: string;
  /**
   * When set, the CTA renders as a button firing this handler instead of a
   * Link — used by pages whose CTA starts an in-place action (e.g. the test
   * landing pages firing the placement auth gate) rather than navigating.
   */
  onCtaClick?: () => void;
}

/**
 * Sticky brand-stack nav used across every Field Day page (it's the only
 * portal nav — the legacy global `Nav` has been retired). Each page renders
 * its own `<FieldNav />`; game hosts render no nav. Reads /me from the
 * shared TanStack Query cache to swap the right-side CTA between "Sign in"
 * and the AuthMenu avatar dropdown.
 */
export function FieldNav({
  ctaLabel = "Sign in",
  ctaTo = "/signin",
  onCtaClick,
}: FieldNavProps = {}): React.ReactElement {
  const { data: me, loading } = useMe();
  const signedIn = !!me?.user;
  const [drawerOpen, setDrawerOpen] = React.useState(false);

  return (
    <nav className="v-field__nav" aria-label="Primary">
      <div className="container v-field__nav-inner">
        <Link className="v-field__brand" to="/">
          {/* Stacked wordmark is decorative (its glyphs read as "SportsAcademyGames"
              with no spaces); hide it from AT and expose a clean accessible name so
              the visible label matches the accessible name (WCAG 2.5.3). */}
          <div className="v-field__brand-stack" aria-hidden="true">
            <div>Sports</div>
            <div>Academy</div>
            <div className="b-games">Games</div>
          </div>
          <span className="visually-hidden">Sports Academy Games</span>
        </Link>
        <div className="v-field__nav-links">
          {NAV_LINKS.map((l) => (
            <Link key={l.to} to={l.to}>
              {l.label}
            </Link>
          ))}
        </div>
        {/* Right cluster — streak badge anchors here (not floating mid-bar),
            then the sign-in CTA / avatar (desktop) and the mobile burger. */}
        <div className="v-field__nav-right">
          <StreakBadge />
          {signedIn ? (
            <AuthMenu me={me} loading={loading} />
          ) : onCtaClick ? (
            <button type="button" className="v-field__nav-cta" onClick={onCtaClick}>
              {ctaLabel} <ArrowIconNav />
            </button>
          ) : (
            <Link to={ctaTo} className="v-field__nav-cta">
              {ctaLabel} <ArrowIconNav />
            </Link>
          )}
          {/* Mobile-only hamburger → shared Drawer. Hidden on desktop via CSS;
              the CTA/AuthMenu above stays the (unchanged) desktop affordance. */}
          <button
            type="button"
            className="vfm__appbar-burger v-field__burger"
            aria-label="Open navigation"
            aria-haspopup="dialog"
            aria-expanded={drawerOpen}
            onClick={() => setDrawerOpen(true)}
          >
            <span className="vfm__burger-bar" />
            <span className="vfm__burger-bar" />
            <span className="vfm__burger-bar" />
          </button>
        </div>
      </div>
      <Drawer open={drawerOpen} onClose={() => setDrawerOpen(false)} signedIn={signedIn} />
    </nav>
  );
}

function ArrowIconNav(): React.ReactElement {
  return (
    <svg
      width="14"
      height="14"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.5"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <line x1="5" y1="12" x2="19" y2="12" />
      <polyline points="12 5 19 12 12 19" />
    </svg>
  );
}
